import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Award, Upload, Eye, Users } from 'lucide-react';
import { imageAssets } from '../assets/imageAssets';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Navigation */}
      <nav className="bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-md opacity-30"></div>
                <img src={imageAssets.imdcLogo} alt="IMDC Creative Works Logo" className="w-14 h-14 rounded-full object-cover relative z-10 ring-2 ring-white shadow-lg" />
              </div>
              <div>
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  IMDC Creative Works System
                </div>
                <div className="text-xs text-gray-500 font-medium tracking-wide">BOHOL ISLAND STATE UNIVERSITY</div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full text-sm font-semibold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                ABOUT US
              </span>
            </div>
            <h1 className="text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                About IMDC Creative Works
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Empowering faculty members to showcase their creative excellence
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* What is IMDC */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8 border-l-4 border-blue-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">What is IMDC Creative Works?</h2>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">
              The <strong className="text-blue-600">IMDC Creative Works Submission System</strong> is an online platform developed for 
              <strong className="text-purple-600"> Bohol Island State University (BISU)</strong> to help faculty members submit, manage, 
              and showcase their creative works. It serves as a digital repository for original creations from the university's talented educators.
            </p>
          </div>

          {/* What You Can Submit */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8 border-l-4 border-purple-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Upload className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">What Can You Submit?</h2>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Faculty members can upload their original creations including:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-rose-50 to-rose-100 p-4 rounded-xl text-center">
                <span className="text-2xl mb-2 block">📜</span>
                <span className="font-semibold text-gray-800">Balak</span>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-xl text-center">
                <span className="text-2xl mb-2 block">📚</span>
                <span className="font-semibold text-gray-800">Children Story</span>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl text-center">
                <span className="text-2xl mb-2 block">📰</span>
                <span className="font-semibold text-gray-800">Editorial Board</span>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl text-center">
                <span className="text-2xl mb-2 block">⚔️</span>
                <span className="font-semibold text-gray-800">Fiction Adventure</span>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl text-center">
                <span className="text-2xl mb-2 block">⚡</span>
                <span className="font-semibold text-gray-800">Flash Fiction</span>
              </div>
              <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-4 rounded-xl text-center">
                <span className="text-2xl mb-2 block">📝</span>
                <span className="font-semibold text-gray-800">Poetry</span>
              </div>
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-xl text-center">
                <span className="text-2xl mb-2 block">✍️</span>
                <span className="font-semibold text-gray-800">Tula</span>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8 border-l-4 border-green-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">How Does It Work?</h2>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">
              Once submitted, your creative works are reviewed by administrators. When approved, 
              they are archived for recognition and preservation. This ensures quality content 
              while giving faculty members the recognition they deserve for their creative talents.
            </p>
          </div>

          {/* About Features: Simple Steps */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8 border-l-4 border-green-500 relative overflow-hidden">
            <div className="absolute top-6 left-6 w-48 h-48 bg-blue-400/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-6 right-6 w-48 h-48 bg-purple-400/5 rounded-full blur-3xl"></div>

            <div className="relative text-center mb-10">
              <div className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
                HOW IT WORKS
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple Steps to Success</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Get your creative works reviewed and archived in just a few easy steps
              </p>
            </div>

            <div className="relative grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="hidden lg:block absolute top-16 left-1/4 w-1/2 h-0.5 bg-gradient-to-r from-blue-300 via-purple-300 to-pink-300"></div>

              <div className="relative text-center group">
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-xl group-hover:shadow-blue-500/50 transition-all transform group-hover:scale-105">
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-blue-600 font-bold text-sm shadow-lg">1</div>
                  <Users className="w-9 h-9 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Create Account</h3>
                <p className="text-gray-600">Register as a faculty member with your BISU credentials</p>
              </div>

              <div className="relative text-center group">
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center shadow-xl group-hover:shadow-purple-500/50 transition-all transform group-hover:scale-105">
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-purple-600 font-bold text-sm shadow-lg">2</div>
                  <Upload className="w-9 h-9 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Upload Work</h3>
                <p className="text-gray-600">Submit your creative works with details and files</p>
              </div>

              <div className="relative text-center group">
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center shadow-xl group-hover:shadow-pink-500/50 transition-all transform group-hover:scale-105">
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-pink-600 font-bold text-sm shadow-lg">3</div>
                  <Eye className="w-9 h-9 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Get Reviewed</h3>
                <p className="text-gray-600">Admin reviews and provides feedback on your submission</p>
              </div>

              <div className="relative text-center group">
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-xl group-hover:shadow-green-500/50 transition-all transform group-hover:scale-105">
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-green-600 font-bold text-sm shadow-lg">4</div>
                  <Award className="w-9 h-9 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Get Recognized</h3>
                <p className="text-gray-600">Approved works are archived and showcased publicly</p>
              </div>
            </div>
          </div>

          {/* About Features: Team Members */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8 border-l-4 border-gray-500">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold mb-4">
                OUR TEAM
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet the Development Team</h2>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                The dedicated professionals behind the IMDC Creative Works Submission System
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-gray-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 pt-6">
                <div className="relative flex justify-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-gray-200 group-hover:ring-gray-400 transition-all shadow-lg">
                    <img src={imageAssets.teamMembers.jimby} alt="James Paolo E. Betarmos" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="absolute -top-1 right-1/4 bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                    Phase 1
                  </div>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">James Paolo E. Betarmos</h3>
                  <p className="text-gray-700 font-semibold">Lead Programmer</p>
                </div>
              </div>

              <div className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-gray-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 pt-6">
                <div className="relative flex justify-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-gray-200 group-hover:ring-gray-400 transition-all shadow-lg">
                    <img src={imageAssets.teamMembers.jhonel} alt="Jhonel B. Igcalinos" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="absolute -top-1 right-1/4 bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                    Phase 1
                  </div>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Jhonel B. Igcalinos</h3>
                  <p className="text-gray-700 font-semibold">Documentator</p>
                </div>
              </div>

              <div className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-gray-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 pt-6">
                <div className="relative flex justify-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-gray-200 group-hover:ring-gray-400 transition-all shadow-lg">
                    <img src={imageAssets.teamMembers.omac} alt="Aldritz I. Omac" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="absolute -top-1 right-1/4 bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                    Phase 1
                  </div>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Aldritz I. Omac</h3>
                  <p className="text-gray-700 font-semibold">Lead Analyst</p>
                </div>
              </div>

              <div className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-gray-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 pt-6">
                <div className="relative flex justify-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-gray-200 group-hover:ring-gray-400 transition-all shadow-lg">
                    <img src={imageAssets.teamMembers.emmanuelKent} alt="Emmanuel Kent P. Paas" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="absolute -top-1 right-1/4 bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                    Phase 1
                  </div>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Emmanuel Kent P. Paas</h3>
                  <p className="text-gray-700 font-semibold">Team Member</p>
                </div>
              </div>

              <div className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-gray-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 pt-6">
                <div className="relative flex justify-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-gray-200 group-hover:ring-gray-400 transition-all shadow-lg">
                    <img src={imageAssets.teamMembers.hecei} alt="Hecei P. Japitana" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="absolute -top-1 right-1/4 bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                    Phase 1
                  </div>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Hecei P. Japitana</h3>
                  <p className="text-gray-700 font-semibold">Team Member</p>
                </div>
              </div>

              <div className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-gray-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 pt-6">
                <div className="relative flex justify-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-gray-200 group-hover:ring-gray-400 transition-all shadow-lg">
                    <img src={imageAssets.teamMembers.jhellJone} alt="Jhell Jone Labonite" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="absolute -top-1 right-1/4 bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                    Phase 1
                  </div>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Jhell Jone Labonite</h3>
                  <p className="text-gray-700 font-semibold">Team Member</p>
                </div>
              </div>

              <div className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-gray-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 pt-6 md:col-span-2 lg:col-span-1 md:max-w-sm md:mx-auto lg:max-w-none lg:mx-0">
                <div className="relative flex justify-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-gray-200 group-hover:ring-gray-400 transition-all shadow-lg">
                    <img src={imageAssets.teamMembers.junmar} alt="Junmar Q. Colinares" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="absolute -top-1 right-1/4 bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                    Phase 1
                  </div>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Junmar Q. Colinares</h3>
                  <p className="text-gray-700 font-semibold">Team Member</p>
                </div>
              </div>
            </div>
          </div>

          {/* Our Goal */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl shadow-xl p-8 md:p-12 text-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Our Goal</h2>
            </div>
            <p className="text-lg leading-relaxed opacity-95">
              Our goal is to provide a simple and organized way for faculty members to share their talents, 
              build their digital portfolio, and contribute to the creative community of the university. 
              We believe every creative work deserves to be seen and appreciated.
            </p>
          </div>

          {/* Back to Home Button */}
          <div className="text-center mt-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
